import { Children, ReactNode } from 'react';

import { Box, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import AliceCarousel from 'react-alice-carousel';
import 'react-alice-carousel/lib/alice-carousel.css';

import FundCarouselContext, { FundCarouselLayout } from './FundCarouselContext';
import { CarouselArrow, CarouselArrowProps } from './components/CarouselArrow';

export {
  FundCarouselCard,
  formatFundCnpj,
} from './components/FundCarouselCard';

interface FundCarouselProps {
  title: string;
  children: ReactNode;
  icon?: ReactNode;
  infinite?: boolean;
  autoPlay?: boolean;
  autoPlayInterval?: number;
  /** When `auto`, section, track and cards follow their content height. */
  height?: FundCarouselLayout;
}

const responsive = {
  0: { items: 1 },
  600: { items: 2 },
  900: { items: 3 },
  1200: { items: 6 },
};

const FIXED_SECTION_HEIGHT = { xs: 'auto', md: 242 } as const;
const FIXED_CONTENT_HEIGHT = { xs: 'auto', md: 210 } as const;

export function FundCarousel({
  title,
  children,
  icon,
  infinite,
  autoPlay,
  autoPlayInterval,
  height = 'fixed',
}: FundCarouselProps) {
  const isAutoHeight = height === 'auto';
  const sectionHeight = isAutoHeight ? 'auto' : FIXED_SECTION_HEIGHT;
  const contentHeight = isAutoHeight ? 'auto' : FIXED_CONTENT_HEIGHT;

  const items = Children.toArray(children).map((child, index) => (
    <Box
      key={index}
      sx={{
        px: { xs: 0.85, md: 0.75 },
        height: isAutoHeight ? 'auto' : '100%',
      }}>
      {child}
    </Box>
  ));

  return (
    <FundCarouselContext.Provider value={height}>
      <Box
        component="section"
        data-height-mode={height}
        sx={{
          px: { xs: 1, md: 1.25 },
          pt: { xs: 1.5, md: 1.5 },
          pb: { xs: 0.4, md: 0.4 },
          height: sectionHeight,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 1,
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
        }}>
        <Stack direction="row" alignItems="center" gap={0.9} mb={0.4}>
          {icon && (
            <Box
              sx={{
                display: 'inline-flex',
                color: 'primary.main',
                '& svg': { fontSize: 18 },
              }}>
              {icon}
            </Box>
          )}
          <Typography
            component="h2"
            color="text.primary"
            fontWeight={700}
            sx={{ fontSize: 14, lineHeight: 1.2 }}>
            {title}
          </Typography>
        </Stack>

        <Box
          sx={{
            position: 'relative',
            height: contentHeight,
            minHeight: isAutoHeight ? 'unset' : undefined,
            flex: isAutoHeight ? '0 0 auto' : undefined,
            display: 'flex',
            alignItems: isAutoHeight ? 'stretch' : 'center',
            px: { xs: 2.5, md: 3.25 },
            '& .alice-carousel': {
              margin: 0,
              width: '100%',
              height: isAutoHeight ? 'auto' : undefined,
            },
            '& .alice-carousel__wrapper': {
              margin: 0,
              padding: 0,
              display: 'flex',
              alignItems: isAutoHeight ? 'stretch' : 'center',
              height: isAutoHeight ? 'auto' : undefined,
            },
            '& .alice-carousel__stage': {
              margin: 0,
              padding: 0,
              height: isAutoHeight ? 'auto' : undefined,
            },
            '& .alice-carousel__stage-item': {
              verticalAlign: 'top',
              height: isAutoHeight ? 'auto' : undefined,
            },
            '& .alice-carousel__prev-btn, & .alice-carousel__next-btn': {
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 24,
              height: 24,
              margin: 0,
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              zIndex: 2,
            },
            '& .alice-carousel__prev-btn': {
              left: 0,
              justifyContent: 'center',
            },
            '& .alice-carousel__next-btn': {
              right: 0,
              justifyContent: 'center',
            },
            '& .alice-carousel__prev-btn-wrapper, & .alice-carousel__next-btn-wrapper':
              {
                display: 'block',
                width: 24,
                height: 24,
              },
          }}>
          <AliceCarousel
            autoPlay={autoPlay}
            autoPlayInterval={autoPlayInterval}
            disableDotsControls
            infinite={infinite}
            items={items}
            mouseTracking
            responsive={responsive}
            renderPrevButton={({ isDisabled }: CarouselArrowProps) => (
              <CarouselArrow direction="previous" isDisabled={isDisabled} />
            )}
            renderNextButton={({ isDisabled }: CarouselArrowProps) => (
              <CarouselArrow direction="next" isDisabled={isDisabled} />
            )}
          />
        </Box>
      </Box>
    </FundCarouselContext.Provider>
  );
}
